import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Rating,
  CircularProgress, Alert, Card, CardContent, Divider, AppBar,
  Toolbar, Avatar, Container
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';

const CustomerOrderList = () => {
  const { storeSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [productRating, setProductRating] = useState(0);
  const [productReview, setProductReview] = useState('');
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [deliveryReview, setDeliveryReview] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem(`store_user_${storeSlug}`);
    if (savedUser) {
      const user = JSON.parse(savedUser);
      setCustomerPhone(user.phone);
      loadOrders(user.phone);
    } else {
      setError('Please login first');
      setLoading(false);
    }
  }, [storeSlug]);

  const loadOrders = async (phone) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/ecommerce-store/public/${storeSlug}/orders/${phone}`);
      if (response.data.success) {
        setOrders(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load orders');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    try {
      setCancelling(true);
      const response = await axios.post(
        `/api/ecommerce-store/public/${storeSlug}/order/${selectedOrder._id}/cancel`,
        { cancellationReason }
      );
      if (response.data.success) {
        setCancelDialogOpen(false);
        setCancellationReason('');
        loadOrders(customerPhone);
      } else {
        alert(response.data.message || 'Failed to cancel order');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitRating = async () => {
    if (productRating === 0 && deliveryRating === 0) {
      alert('Please provide at least one rating');
      return;
    }

    try {
      setSubmittingRating(true);
      const response = await axios.post(
        `/api/ecommerce-store/public/${storeSlug}/order/${selectedOrder._id}/rate`,
        {
          productRating: productRating || undefined,
          productReview,
          deliveryRating: deliveryRating || undefined,
          deliveryReview
        }
      );
      if (response.data.success) {
        setRatingDialogOpen(false);
        setProductRating(0);
        setProductReview('');
        setDeliveryRating(0);
        setDeliveryReview('');
        loadOrders(customerPhone);
        alert('Thank you for your feedback!');
      } else {
        alert(response.data.message || 'Failed to submit rating');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'warning',
      CONFIRMED: 'info',
      PREPARING: 'primary',
      READY: 'success',
      OUT_FOR_DELIVERY: 'secondary',
      DELIVERED: 'success',
      CANCELLED: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      PENDING: <AccessTimeIcon fontSize="small" />,
      CONFIRMED: <CheckCircleIcon fontSize="small" />,
      PREPARING: <RestaurantIcon fontSize="small" />,
      READY: <CheckCircleIcon fontSize="small" />,
      OUT_FOR_DELIVERY: <LocalShippingIcon fontSize="small" />,
      DELIVERED: <CheckCircleIcon fontSize="small" />,
      CANCELLED: <CancelIcon fontSize="small" />
    };
    return icons[status] || <ShoppingBagIcon fontSize="small" />;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate(`/store/${storeSlug}`)}>
          Go to Store
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <AppBar position="sticky" sx={{ bgcolor: 'white', color: 'black', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate(`/store/${storeSlug}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            <Typography variant="h6" fontWeight={700}>
              My Orders
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        {orders.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ShoppingBagIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No orders yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start shopping to see your orders here
            </Typography>
            <Button variant="contained" onClick={() => navigate(`/store/${storeSlug}`)}>
              Browse Products
            </Button>
          </Paper>
        ) : (
          orders.map((order) => {
            const orderId = String(order._id);
            const orderNumber = orderId.slice(-8).toUpperCase();
            const canCancel = order.status === 'PENDING';
            const canRate = order.status === 'DELIVERED' && !order.productRating && !order.deliveryRating;
            const isDelivered = order.status === 'DELIVERED';
            const isCancelled = order.status === 'CANCELLED';

            return (
              <Card key={orderId} sx={{ mb: 2, overflow: 'visible' }}>
                <CardContent>
                  {/* Order Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Order #{orderNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(order.createdAt)}
                      </Typography>
                    </Box>
                    <Chip
                      label={order.status}
                      color={getStatusColor(order.status)}
                      icon={getStatusIcon(order.status)}
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Order Items */}
                  <Box sx={{ mb: 2 }}>
                    {order.items?.slice(0, 3).map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          {item.name} × {item.qty}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          ${(item.price * item.qty).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                    {order.items?.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        +{order.items.length - 3} more items
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1" fontWeight={700}>
                      Total
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="primary">
                      ${order.total?.toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Ratings Display */}
                  {(order.productRating || order.deliveryRating) && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Your Rating
                      </Typography>
                      {order.productRating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption">Product:</Typography>
                          <Rating value={order.productRating} size="small" readOnly />
                        </Box>
                      )}
                      {order.deliveryRating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption">Delivery:</Typography>
                          <Rating value={order.deliveryRating} size="small" readOnly />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<TrackChangesIcon />}
                      onClick={() => navigate(`/store/${storeSlug}/track/${orderId}`)}
                      sx={{ flex: 1, minWidth: 120 }}
                    >
                      Track Order
                    </Button>

                    {canCancel && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setSelectedOrder(order);
                          setCancelDialogOpen(true);
                        }}
                        sx={{ flex: 1, minWidth: 120 }}
                      >
                        Cancel
                      </Button>
                    )}

                    {canRate && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<StarIcon />}
                        onClick={() => {
                          setSelectedOrder(order);
                          setRatingDialogOpen(true);
                        }}
                        sx={{ flex: 1, minWidth: 120 }}
                      >
                        Rate Order
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Container>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide a reason for cancelling this order:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="e.g., Changed my mind, Ordered by mistake, etc."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Keep Order
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelOrder}
            disabled={cancelling || !cancellationReason.trim()}
          >
            {cancelling ? <CircularProgress size={24} /> : 'Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={ratingDialogOpen} onClose={() => setRatingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rate Your Order</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Product Quality
            </Typography>
            <Rating
              value={productRating}
              onChange={(e, newValue) => setProductRating(newValue)}
              size="large"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              value={productReview}
              onChange={(e) => setProductReview(e.target.value)}
              placeholder="Tell us about the product quality..."
              sx={{ mt: 1 }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Delivery Service
            </Typography>
            <Rating
              value={deliveryRating}
              onChange={(e, newValue) => setDeliveryRating(newValue)}
              size="large"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              value={deliveryReview}
              onChange={(e) => setDeliveryReview(e.target.value)}
              placeholder="Tell us about the delivery service..."
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitRating}
            disabled={submittingRating || (productRating === 0 && deliveryRating === 0)}
          >
            {submittingRating ? <CircularProgress size={24} /> : 'Submit Rating'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerOrderList;
